import type { Entity } from '@backstage/catalog-model';

export type WorkspaceCreationDefaults = Readonly<{
  templateName?: string;
  workspaceName?: string;
  params: Readonly<Record<string, string>>;
}>;

const TECHDOCS_REF_ANNOTATION = 'backstage.io/techdocs-ref';
const SOURCE_LOCATION_ANNOTATION = 'backstage.io/source-location';
const TECHDOCS_TEMPLATE_NAME = 'ipen-techdocs';
const DAH_WORKSPACE_TEMPLATE_NAME = 'dah-enablement';
const DAH_WORKSPACE_TYPE = 'dah-workspace';

function repositoryNameFromLocation(location: string): string | undefined {
  if (!location.startsWith('url:')) {
    return undefined;
  }

  try {
    const url = new URL(location.slice('url:'.length));
    const repositoryPath = url.pathname.split('/-/')[0].replace(/\/+$/, '');
    const repositoryName = repositoryPath
      .split('/')
      .pop()
      ?.replace(/\.git$/, '');
    return repositoryName ? decodeURIComponent(repositoryName) : undefined;
  } catch {
    return undefined;
  }
}

function dahRepositoryUrlFromLocation(location: string): string | undefined {
  if (!location.startsWith('url:')) {
    return undefined;
  }

  try {
    const url = new URL(location.slice('url:'.length));
    const repositoryPath = url.pathname.split('/-/')[0].replace(/\/+$/, '');
    if (!repositoryPath) {
      return undefined;
    }

    const cloneRepositoryPath = repositoryPath
      .replace(/\.git$/, '')
      .replace(/\/infrastructure$/, '/monorepo');
    url.pathname = `${cloneRepositoryPath}.git`;
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return undefined;
  }
}

function isDahWorkspaceEntity(entity: Entity): boolean {
  const kind = entity.kind.toLocaleLowerCase('en-US');
  const type = entity.spec?.type;

  return (
    kind === 'resource' &&
    typeof type === 'string' &&
    type.toLocaleLowerCase('en-US') === DAH_WORKSPACE_TYPE
  );
}

export function isCoderWorkspaceEntity(entity: Entity): boolean {
  return (
    entity.kind.toLocaleLowerCase('en-US') === 'system' ||
    isDahWorkspaceEntity(entity)
  );
}

/**
 * Build workspace defaults from standard Backstage metadata. DAH workspace
 * Resources use source-location to identify their solution's monorepo. For Systems,
 * a URL techdocs ref identifies the documentation repository most directly and
 * source-location is the fallback when the TechDocs reference is relative.
 */
export function entityWorkspaceDefaults(
  entity: Entity,
): WorkspaceCreationDefaults {
  const annotations = entity.metadata.annotations ?? {};

  if (isDahWorkspaceEntity(entity)) {
    const repositoryUrl = dahRepositoryUrlFromLocation(
      annotations[SOURCE_LOCATION_ANNOTATION] ?? '',
    );

    return {
      templateName: DAH_WORKSPACE_TEMPLATE_NAME,
      workspaceName: entity.metadata.name,
      params: repositoryUrl
        ? { repository_urls: JSON.stringify([repositoryUrl]) }
        : {},
    };
  }

  const repository = [
    annotations[TECHDOCS_REF_ANNOTATION],
    annotations[SOURCE_LOCATION_ANNOTATION],
  ]
    .map(location =>
      location ? repositoryNameFromLocation(location) : undefined,
    )
    .find((name): name is string => Boolean(name));

  return {
    templateName: TECHDOCS_TEMPLATE_NAME,
    params: repository ? { repositories: JSON.stringify([repository]) } : {},
  };
}
